<template>
  <v-container fluid grid-list-md>
    <ingredients-table
      :edit="editLocal"
      :ingredients.sync="ingredients"
      :permissions="permissions"
    />

    <ferments-table
      :edit="editLocal"
      :ferments.sync="ferments"
      :selectedParameters="selectedParameters"
      :permissions="permissions"
    />

    <v-data-iterator
      :items="recipes"
      :rows-per-page-items="rowsPerPageItems"
      :pagination.sync="pagination"
      class="mt-3"
      content-tag="v-layout"
      row
      wrap
    >
      <template v-slot:header>
        <v-card class="mb-3">
          <v-card-title>
            <h2>Recipes</h2>
          </v-card-title>
        </v-card>
      </template>

      <template v-slot:item="props">
        <v-flex xs12 sm6 md4>
          <recipe
            :recipe="props.item"
            :edit="editLocal"
            :permissions="permissions"
            :maxRecipesCount="trial.planned_recipes"
            :recipesCount="recipes.length"
            @editRecipe="showEditRecipePopup"
          />
        </v-flex>
      </template>

      <template v-if="permissions.EDIT || permissions.RECIPE_FILLING_RESULTS" v-slot:actions-append>
        <template v-if="editLocal">
          <v-tooltip v-if="recipes.length < trial.planned_recipes" bottom>
            <template v-slot:activator="{ on }">
              <v-btn
                fab
                small
                color="success"
                :disabled="isSendingRecipes || isResetRecipes"
                v-on="on"
                @click="showNewRecipePopup"
              >
                <v-icon>add</v-icon>
              </v-btn>
            </template>
            <span>New recipe</span>
          </v-tooltip>
        </template>

        <template v-if="editLocal || permissions.RECIPE_FILLING_RESULTS">
          <v-btn
            @click="resetRecipes"
            :loading="isResetRecipes"
            :disabled="isResetRecipes || isSendingRecipes"
            flat
            color="warning"
          >
            Reset
          </v-btn>
          <v-btn
            :loading="isSendingRecipes"
            :disabled="isSendingRecipes || isResetRecipes"
            @click="saveRecipes"
            color="success"
          >
            Save
          </v-btn>
        </template>
      </template>
    </v-data-iterator>

    <template v-if="permissions.EDIT">
      <new-recipe-popup
        :dialog.sync="newRecipePopupStatus"
        :ingredients="ingredients"
        :ferments="ferments"
        :process="process"
        :parameters="selectedParameters"
        @newRecipe="createNewRecipe"
      />
      <edit-recipe-popup
        :dialog.sync="editRecipePopupStatus"
        :ingredients="ingredients"
        :ferments="ferments"
        :process="process"
        :parameters="selectedParameters"
        :recipe="selectedRecipe"
        @updateRecipe="updateCurrentRecipe"
      />
    </template>
  </v-container>
</template>

<script>
import Process from '@/orm/models/Process';
import Trial from '@/orm/models/Trial';
import Recipes from '@/orm/models/Recipes';
import RecipeModule from '@/orm/models/RecipeModule';

export default {
  name: 'recipes',
  props: {
    trial_id: [String, Number],
    edit: {
      type: Boolean,
      default: () => false,
    },
    permissions: Object,
  },
  async created() {
    await this.replayFetch();
  },
  data() {
    return {
      rowsPerPageItems: [
        3,
        { 'text': '$vuetify.dataIterator.rowsPerPageAll', 'value': -1 },
      ],
      pagination: {
        rowsPerPage: -1,
      },
      ingredients: [],
      ferments: [],
      newRecipePopupStatus: false,
      editRecipePopupStatus: false,
      isResetRecipes: false,
      isSendingRecipes: false,
      selectedRecipe: null,
    };
  },
  watch: {
    async currentGroup() {
      await this.resetRecipes();
    },
  },
  computed: {
    editLocal: {
      get() {
        return this.edit;
      },
      set(val) {
        this.$emit('update:edit', val);
      },
    },
    selectedParameters() {
      return this.ingredients.length && this.ingredients[0].selectedParameters
        ? [...this.ingredients[0].selectedParameters]
        : [];
    },
    recipes() {
      return Recipes.query().with('process').get();
    },
    process() {
      return Process.all();
    },
    trial() {
      return Trial.find(+this.trial_id);
    },
  },
  methods: {
    showNewRecipePopup() {
      this.newRecipePopupStatus = true;
    },
    async createNewRecipe({ recipe }) {
      await Recipes.insert({ data: recipe });
    },
    async updateCurrentRecipe({ recipe }) {
      this.selectedRecipe = null;
      await Recipes.insertOrUpdate({ data: JSON.parse(JSON.stringify(recipe)) });
    },
    showEditRecipePopup(recipeId) {
      this.editRecipePopupStatus = true;
      this.selectedRecipe = Recipes.find(+recipeId).$toJson();
    },
    goToEditRecipes() {
      this.editLocal = true;
    },
    setIngredientsAndFerments(recipes) {
      this.ingredients = recipes.ingredients ? JSON.parse(JSON.stringify(recipes.ingredients)) : [];
      this.ferments = recipes.ferments ? JSON.parse(JSON.stringify(recipes.ferments)) : [];
    },
    async replayFetch() {
      const { recipes } = await RecipeModule.$get({
        params: {
          id: this.trial_id,
        },
      });
      this.setIngredientsAndFerments(recipes);
      return recipes;
    },
    async resetRecipes() {
      try {
        this.isResetRecipes = true;
        const recipes = await this.replayFetch();
        await Recipes.create({
          data: recipes.items || [],
        });
      } finally {
        this.isResetRecipes = false;
        this.editLocal = false;
      }
    },
    async saveRecipes() {
      try {
        const recipesLocal = this.recipes;
        this.isSendingRecipes = true;
        const { recipes } = await RecipeModule.$create({
          params: {
            id: this.trial_id,
          },
          data: {
            recipes: {
              items: recipesLocal,
              ingredients: this.ingredients,
              ferments: this.ferments,
            },
          },
        });
        this.setIngredientsAndFerments(recipes);
        await Recipes.create({
          data: recipes.items || [],
        });
      } finally {
        this.isSendingRecipes = false;
        this.editLocal = false;
      }
    },
  },
  components: {
    editRecipePopup: () => import('./editRecipePopup'),
    ingredientsTable: () => import('./ingredients'),
    fermentsTable: () => import('./ferments'),
    newRecipePopup: () => import('./newRecipePopup'),
    recipe: () => import('./recipe'),
  },
};
</script>
